import { NgClass, UpperCasePipe } from '@angular/common';
import {
  Component,
  computed,
  effect,
  HostListener,
  inject,
  input,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  ContextService,
  FeatureService,
  GlobalStore,
  TrackEventService,
} from '@maximizer/core/shared/data-access';
import { DefaultDataProcessingFeatures } from '@maximizer/ai/data-access';
import { GlobalServicesLoginDetails } from '@maximizer/core/shared/domain';
import { InsightsService } from '@maximizer/core/shared/insights';
import { UI_LOADER } from '@maximizer/core/shared/ui/components/loader';
import { UI_KENDO } from '@maximizer/core/shared/ui/kendo';
import {
  IS_BROWSER_ONLY_ROUTE,
  OAuth2Service,
} from '@maximizer/oauth2/data-access';
import {
  AppDirectoryService,
  UserSyncConfiguration,
  UserSyncConfigurationService,
} from '@maximizer/outlook-sync/data-access';
import {
  MsalAccountWarningComponent,
  MsalLoginMessageComponent,
  MsalMismatchErrorComponent,
  MsalMismatchMessageComponent,
} from '@maximizer/outlook/auth/ui';
import { OUTLOOK_AI_UI } from '@maximizer/outlook/ai/ui';
import { isAdminAnonymizationEnabled } from '@maximizer/outlook/ai/data-access';
import {
  CelebrationComponent,
  ExitComponent,
  OutlookStepperComponent,
  RemindLaterComponent,
} from '@maximizer/outlook/onboarding/ui';
import { detectBrowser, detectOS } from '@maximizer/shared/util';
import {
  AbEntryService,
  EmailBuilderService,
  NavigationService,
  OnboardingService,
  OnboardingStore,
  OutlookMsalService,
  OutlookService,
  OutlookSessionService,
  OutlookStore,
  releasePopupOpen,
  respondToHandshakeFromOpener,
  generateHandshakeNonce,
  SYNC_CONFIG_HANDSHAKE_NONCE_PARAM,
  SYNC_CONFIG_LANG_PARAM,
  tryClaimPopupOpen,
} from '@maximizer/outlook/shared/data-access';
import {
  EventAction,
  EventJourney,
  EventModule,
} from '@maximizer/outlook/shared/domain';
import {
  MenuItem,
  MenuType,
  OUTLOOK_SHARED_UI,
  OutlookNotificationComponent,
} from '@maximizer/outlook/shared/ui';
import {
  SyncInstallComponent,
  SyncSettingsComponent,
} from '@maximizer/outlook/sync/ui';
import { UI_PIPES } from '@maximizer/core/shared/ui/pipes';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  DropDownButtonComponent,
  KENDO_BUTTONS,
} from '@progress/kendo-angular-buttons';
import { KENDO_DIALOG } from '@progress/kendo-angular-dialog';
import { KENDO_TOOLTIP, KENDO_TOOLTIPS } from '@progress/kendo-angular-tooltip';
import { catchError, EMPTY, finalize, of, tap } from 'rxjs';

@Component({
  selector: 'maximizer-outlook-menu',
  templateUrl: './menu.component.html',
  imports: [
    CelebrationComponent,
    ExitComponent,
    KENDO_BUTTONS,
    KENDO_DIALOG,
    KENDO_TOOLTIP,
    KENDO_TOOLTIPS,
    MsalAccountWarningComponent,
    MsalLoginMessageComponent,
    MsalMismatchErrorComponent,
    MsalMismatchMessageComponent,
    NgClass,
    OutlookStepperComponent,
    OUTLOOK_SHARED_UI,
    RemindLaterComponent,
    SyncInstallComponent,
    SyncSettingsComponent,
    TranslatePipe,
    UI_KENDO,
    UI_LOADER,
    UI_PIPES,
    UpperCasePipe,
    OUTLOOK_AI_UI,
  ],
})
export class OutlookMenuComponent {
  @HostListener('window:blur')
  onWindowBlur(): void {
    const dropdown = this.menuDropdown();
    if (dropdown.toggle) {
      dropdown.toggle(false);
    }
  }

  menuDropdown = viewChild.required<DropDownButtonComponent>('menuDropdown');
  notification =
    viewChild.required<OutlookNotificationComponent>('notification');

  isAuthorizedRoute = input<boolean>(false);
  pageTitle = input<string>('');
  canNavigateBack = input<boolean>(false);
  deeplink = input<string>(
    this.contextService.website || 'https://www.maximizer.com',
  );

  private readonly userGuideUrl =
    'https://support.maximizer.com/hc/en-us/articles/29776800842253-Microsoft-Outlook-Integration-Getting-Started-Guide-Maximizer-AI-CRM-app-for-Outlook';

  private readonly SYNC_ERROR_DISMISSED_KEY = 'syncErrorDismissed';
  private readonly DISMISSAL_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 1 week in milliseconds

  private hasInitialized = false;
  private lastSyncedMailboxType: boolean | null = null;
  private syncInstallMenuItem!: MenuItem;
  private statusEnabledMenuItem!: MenuItem;
  private statusErrorMenuItem!: MenuItem;
  private syncSharedMailboxWarningMenuItem!: MenuItem;

  menu: MenuItem[] = [];
  outlookSyncInstalled = false;
  outlookSyncFeatureFlagEnabled = false;
  showRemindLater = false;
  startOnboarding = false;
  supportedOnboardingPage = true;
  mailboxAccount = this.outlookService.mailbox?.userProfile.emailAddress ?? '';
  hideMsalMismatchDialog = false;
  hideMsalLoginDialog = false;
  openSyncSettings = false;
  loginDetails?: GlobalServicesLoginDetails;
  openInstallSync = false;
  /** `true` on `/sync-configuration`: skips MSAL init and hides all MSAL dialogs. */
  readonly disableMsalAuth = inject(IS_BROWSER_ONLY_ROUTE);
  private readonly appDirectoryService = inject(AppDirectoryService);

  loading = signal(false);
  showSyncStatusMessage = signal(false);
  dismissSyncStatusMessage = signal(false);
  showPrivacySecurity = signal(false);

  showOnboardingDialog = computed(() =>
    this.onboardingService.showOnboardingDialog(),
  );
  hasActiveAccount = computed(() => this.outlookStore.hasActiveAccount());
  isIqBoostRoute = computed(() =>
    this.navigationService.currentUrl().startsWith('/iq-boost'),
  );

  readonly showAnonymizer = computed(
    () =>
      isAdminAnonymizationEnabled(
        this.outlookStore.adminDataProcessingFeatures(),
      ) || this.outlookStore.userAnonymizationEnabled(),
  );
  readonly showDataSource = computed(
    () =>
      !(
        this.outlookStore.adminDataProcessingFeatures() ??
        DefaultDataProcessingFeatures
      ).useWebData,
  );
  readonly showServerRegion = computed(
    () =>
      !(
        (
          this.outlookStore.adminDataProcessingFeatures() ??
          DefaultDataProcessingFeatures
        ).useGlobalServers && this.outlookStore.userGlobalServersEnabled()
      ),
  );
  readonly showPrivacySecurityButton = computed(() => this.isIqBoostRoute());

  readonly anonymizerStatus = computed(() =>
    this.translate.instant(
      this.showAnonymizer()
        ? 'outlook.iqBoost.privacySecurity.anonymizerActive'
        : 'outlook.iqBoost.privacySecurity.anonymizerInactive',
    ),
  );
  readonly dataSource = computed(() =>
    this.translate.instant(
      this.showDataSource()
        ? 'outlook.iqBoost.privacySecurity.maximizerOnly'
        : 'outlook.iqBoost.privacySecurity.webData',
    ),
  );
  readonly serverRegion = computed(() =>
    this.translate.instant(
      this.showServerRegion()
        ? 'outlook.iqBoost.privacySecurity.canadaServers'
        : 'outlook.iqBoost.privacySecurity.globalServers',
    ),
  );

  constructor(
    private readonly contextService: ContextService,
    private readonly session: OutlookSessionService,
    private readonly router: Router,
    private readonly globalStore: GlobalStore,
    private readonly oAuthService: OAuth2Service,
    private readonly translate: TranslateService,
    private readonly outlookSyncService: UserSyncConfigurationService,
    private readonly outlookService: OutlookService,
    private readonly msalService: OutlookMsalService,
    private readonly insightsService: InsightsService,
    private readonly tracker: TrackEventService,
    private readonly navigationService: NavigationService,
    private readonly abEntryService: AbEntryService,
    private readonly emailBuilderService: EmailBuilderService,
    public onboardingStore: OnboardingStore,
    public onboardingService: OnboardingService,
    public feature: FeatureService,
    public outlookStore: OutlookStore,
  ) {
    this.outlookSyncFeatureFlagEnabled = feature.isFeatureOn(
      'microsoft-outlook-sync-emails-shown',
      false,
    );

    effect(() => {
      if (this.isAuthorizedRoute() && !this.hasInitialized) {
        this.initializeMenuLogic();
        this.hasInitialized = true;
        this.lastSyncedMailboxType = this.outlookStore.isSharedMailbox();
      }
    });

    effect(() => {
      const isSharedMailbox = this.outlookStore.isSharedMailbox();

      if (!this.hasInitialized) return;
      if (this.lastSyncedMailboxType === isSharedMailbox) return;

      this.lastSyncedMailboxType = isSharedMailbox;

      // The rebuild reads/writes store signals via getOutlookSyncSettings();
      // untracked() keeps isSharedMailbox as this effect's only dependency.
      untracked(() => this.rebuildMenuForMailboxSwitch());
    });

    effect(() => {
      if (!this.outlookStore.hasActiveAccount()) return;

      if (this.mailboxAccount === this.outlookStore.graphMailAccount()) {
        this.outlookStore.setDoesActiveAccountMatch(true);
      } else {
        this.outlookStore.setDoesActiveAccountMatch(false);
      }
    });

    effect(() => {
      if (!this.outlookStore.syncHealthCheckRequested()) return;

      this.outlookStore.clearSyncHealthCheckRequest();

      const config = this.outlookStore.syncConfiguration();
      if (!config?.id || !config.enabled) return;

      this.checkSyncHealthStatus(config.id);
    });
  }

  private initializeMenuLogic(): void {
    this.mailboxAccount =
      this.outlookService.mailbox?.userProfile.emailAddress ?? '';
    this.loginDetails = this.oAuthService.getStorageLoginDetails();

    this.initializeDefaultMenuItems();
    this.initializeOptionalMenuItems();

    this.buildSyncMenuItems();

    if (this.outlookService.isCompose || this.outlookService.isCalendarItem) {
      this.supportedOnboardingPage = false;
    }

    if (!this.disableMsalAuth) {
      this.initializeMsalService();
    }
  }

  /** Builds the sync-related menu items for the current mailbox type. */
  private buildSyncMenuItems(): void {
    if (!this.outlookStore.isSharedMailbox()) {
      this.getOutlookSyncSettings();
    } else if (
      this.outlookStore.isCloud() &&
      this.outlookSyncFeatureFlagEnabled
    ) {
      this.addMenuItem(this.syncSharedMailboxWarningMenuItem);
    }
  }

  private rebuildMenuForMailboxSwitch(): void {
    this.menu = [];
    this.initializeDefaultMenuItems();
    this.initializeOptionalMenuItems();
    this.buildSyncMenuItems();
  }

  async initializeMsalService(): Promise<void> {
    this.hideMsalMismatchDialog =
      localStorage.getItem('hideMismatchMessage') === 'true';

    this.hideMsalLoginDialog =
      localStorage.getItem('hideLoginMessage') === 'true';

    await this.msalService.initializeMsal();
    this.msalService.checkAndSetActiveAccount();

    if (this.hideMsalLoginDialog && !this.hasActiveAccount()) {
      this.msalService.loginPopup();
    }
  }

  private initializeDefaultMenuItems(): void {
    this.menu = [
      {
        id: MenuType.Refresh,
        text: this.translate.instant('outlook.menu.refresh'),
        icon: 'fa-icons icon-solid icon-14 icon-refresh',
        click: () => {
          this.refresh();
          this.trackMenuEvent(MenuType.Refresh);
        },
      },
      {
        id: MenuType.Maximizer,
        text: this.translate.instant('outlook.menu.maximizer'),
        icon: 'fa-icons icon-solid icon-14 icon-arrow-up-right-from-square',
        click: () => {
          window.open(this.deeplink(), '_blank', 'noopener');
          this.trackMenuEvent(MenuType.Maximizer);
        },
      },
      {
        id: MenuType.Preferences,
        text: this.translate.instant('outlook.menu.preferences'),
        icon: 'fa-icons icon-solid icon-14 icon-sliders',
        click: () => {
          this.router.navigate(['/preferences']);
          this.trackMenuEvent(MenuType.Preferences);
        },
      },
      {
        id: MenuType.Guide,
        text: this.translate.instant('outlook.menu.guide'),
        icon: 'fa-icons icon-14 icon-book',
        click: () => {
          window.open(this.userGuideUrl, '_blank', 'noopener');
          this.trackMenuEvent(MenuType.Guide);
        },
      },
      {
        id: MenuType.Logout,
        text: this.translate.instant('outlook.menu.logout'),
        icon: 'fa-icons icon-solid icon-14 icon-right-from-bracket',
        click: () => {
          this.logout();
          this.trackMenuEvent(MenuType.Logout);
        },
      },
      {
        id: MenuType.User,
        text: this.globalStore.session()?.user.displayName ?? '',
        title: this.oAuthService.mxUserEmail ?? '',
        tag: 'userProfile',
        class: 'user',
        disabled: true,
      },
      {
        id: MenuType.Database,
        text: this.contextService.alias,
        icon: 'fa-icons icon-14 icon-database',
        class: 'database',
        disabled: true,
      },
      {
        id: MenuType.AppVersion,
        text: `${this.translate.instant('outlook.menu.appVersion')} ${this.contextService.version}`,
        class: 'app-version',
        disabled: true,
      },
    ];

    if (!this.outlookStore.isSharedMailbox()) {
      this.addMenuItem({
        id: MenuType.Settings,
        text: this.translate.instant('outlook.menu.settings'),
        icon: 'fa-icons icon-14 icon-gear',
        click: () => {
          this.openSyncSettings = true;
          this.trackMenuEvent(MenuType.Settings);
        },
        class: 'sync-settings',
        disabled: true,
      });
    }

    if (
      !this.outlookStore.isCloud() ||
      !this.outlookSyncFeatureFlagEnabled ||
      !this.outlookStore.versionValidForNewSync()
    ) {
      this.removeMenuItem(MenuType.Settings);
    }
  }

  private initializeOptionalMenuItems(): void {
    this.syncInstallMenuItem = {
      id: MenuType.EnableSync,
      text: this.translate.instant('outlook.menu.enableSync'),
      icon: 'fa-icons icon-14 icon-box-open',
      click: () => {
        this.openInstallSync = true;
        this.trackMenuEvent(MenuType.EnableSync);
      },
    };

    this.statusEnabledMenuItem = {
      id: MenuType.SyncStatus,
      text: this.translate.instant('outlook.menu.syncStatusEnabled'),
      icon: 'fa-icons icon-14 icon-solid icon-circle-check',
      class: 'sync-status-enabled justify-center',
      click: () => {
        this.openSyncSettings = true;
        this.trackMenuEvent(MenuType.SyncStatus);
      },
    };

    this.statusErrorMenuItem = {
      id: MenuType.SyncStatus,
      text: this.translate.instant('outlook.menu.syncStatusError'),
      icon: 'fa-icons icon-14 icon-solid icon-circle-exclamation',
      class: 'sync-status-error justify-center',
      click: () => {
        this.reinstallOutlookSync();
        this.trackMenuEvent(MenuType.ReInstallSync);
      },
      tag: 'syncError',
    };

    this.syncSharedMailboxWarningMenuItem = {
      id: MenuType.SyncSharedMailboxWarning,
      text: this.translate
        .instant('outlook.menu.syncSharedMailboxWarning')
        .trim(),
      class: 'outlook-warning text-body',
    };
  }

  private addMenuItem(menuItem: MenuItem): void {
    if (!this.menu.some((item) => item.id === menuItem.id)) {
      this.menu.unshift(menuItem);
    }
  }

  private toggleDisabledMenuItem(menuId: MenuType, disabled: boolean): void {
    const menuItem = this.menu.find((item) => item.id === menuId);
    if (menuItem) {
      menuItem.disabled = disabled;
    }
  }

  private removeMenuItem(menuItem: MenuType): void {
    this.menu = [...this.menu.filter((item) => item.id !== menuItem)];
  }

  /** Read sync settings from store; pass `refreshConfig=true` to force an API fetch. */
  getOutlookSyncSettings(refreshConfig = false): void {
    const previousSyncInstallationState = this.outlookSyncInstalled;
    const session = this.globalStore.session();
    const storeSyncConfig = this.outlookStore.syncConfiguration();

    if (
      !this.outlookStore.isCloud() ||
      !this.outlookSyncFeatureFlagEnabled ||
      !this.outlookStore.versionValidForNewSync()
    ) {
      return;
    }

    if (storeSyncConfig && !refreshConfig) {
      this.handleSyncConfig(storeSyncConfig);

      if (previousSyncInstallationState !== this.outlookSyncInstalled) {
        this.reinitializeMenu();
      }
      return;
    }

    if (session) {
      this.outlookSyncService
        .getConfiguration(session.user.id, session.workspace)
        .pipe(
          tap((config) => {
            if (config) {
              this.outlookStore.updateSyncConfiguration(config);
              this.handleSyncConfig(config);
            } else {
              this.outlookSyncInstalled = false;
              this.addMenuItem(this.syncInstallMenuItem);
            }

            if (previousSyncInstallationState !== this.outlookSyncInstalled) {
              this.reinitializeMenu();
            }
          }),
          catchError((error) => {
            // 401/500/network: state unknown, preserve prior menu instead of falsely showing install.
            console.warn(
              'Failed to fetch sync configuration for menu; preserving prior state',
              error,
            );
            return EMPTY;
          }),
        )
        .subscribe();
    }
  }

  private handleSyncConfig(
    config: UserSyncConfiguration,
    skipHealthCheck = false,
  ): void {
    this.outlookSyncInstalled = !!config?.enabled;

    if (this.outlookSyncInstalled) {
      if (!skipHealthCheck) {
        this.checkSyncHealthStatus(config.id);
      }
      this.toggleDisabledMenuItem(MenuType.Settings, false);
    } else {
      this.addMenuItem(this.syncInstallMenuItem);
    }
  }

  private checkSyncHealthStatus(id: string): void {
    if (this.outlookStore.isSharedMailbox()) return;

    this.outlookSyncService
      .getHealthStatus(id)
      .pipe(catchError(() => of(undefined)))
      .subscribe((status) => {
        // The mailbox may have switched to shared while this request was in
        // flight; don't add a stale sync-status badge to a shared mailbox.
        if (this.outlookStore.isSharedMailbox()) return;

        this.removeMenuItem(MenuType.SyncStatus); // clear old status before update

        if (status === undefined) {
          return;
        }

        const hasError =
          !status.isMicrosoftAuthHealthy || !status.isMaximizerAuthHealthy;

        this.outlookStore.setHasSyncErrors(hasError);
        this.outlookStore.setSyncHealthLastChecked(Date.now());

        this.showSyncStatusMessage.set(
          hasError && !this.isSyncErrorDismissed(),
        );

        if (hasError) {
          this.addMenuItem(this.statusErrorMenuItem);
        } else {
          this.clearSyncErrorDismissed();
          this.addMenuItem(this.statusEnabledMenuItem);
        }
      });
  }

  logout(): void {
    this.session.logout('menu-logout');
  }

  back(): void {
    this.navigationService.navigateBack();
  }

  refresh(): void {
    this.loading.set(true);

    this.session.clearSessionCache();
    this.session
      .getInfo()
      .pipe(
        tap((session) => {
          if (!session) {
            return;
          }
          this.abEntryService
            .getLastContactedPreferences()
            .pipe(
              tap((preferences) => {
                this.outlookStore.setLastContactDatePreferences(preferences);
                this.getOutlookSyncSettings(true);
              }),
              catchError((error) => {
                const errorObj =
                  error instanceof Error ? error : new Error(String(error));
                this.insightsService.trackException({
                  error: errorObj,
                  properties: {
                    component: 'OutlookMenuComponent',
                    action: 'menu-refresh-last-contacted-preferences',
                  },
                });
                return of(null);
              }),
            )
            .subscribe();
        }),
        catchError((error) => {
          const errorObj =
            error instanceof Error ? error : new Error(String(error));
          this.insightsService.trackException({
            error: errorObj,
            properties: {
              component: 'OutlookMenuComponent',
              action: 'menu-refresh-session-info',
            },
          });
          return of(null);
        }),
        finalize(() => {
          this.loading.set(false);
          this.navigateToRefreshUI();
        }),
      )
      .subscribe();
  }

  private navigateToRefreshUI(): void {
    const currentUrl = this.router.url;
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigateByUrl(currentUrl);
    });
  }

  private reinitializeMenu(): void {
    this.menu = [];
    this.initializeDefaultMenuItems();
    this.initializeOptionalMenuItems();

    if (!this.outlookStore.isSharedMailbox()) {
      const storeSyncConfig = this.outlookStore.syncConfiguration();

      if (storeSyncConfig) {
        this.handleSyncConfig(storeSyncConfig, true);
      }
    } else if (
      this.outlookStore.isCloud() &&
      this.outlookSyncFeatureFlagEnabled
    ) {
      this.addMenuItem(this.syncSharedMailboxWarningMenuItem);
    }
  }

  openOnboarding(): void {
    this.trackMenuEvent('onboarding');
    this.onboardingService.showOnboardingDialog.set(true);
  }

  trackMenuEvent(item: string): void {
    this.tracker.trackEvent({
      module: EventModule.Menu,
      journey: EventJourney.Action,
      action: EventAction.Clicked,
      metadata: {
        option: item,
      },
    });
  }

  onSyncStatusMessageLinkClick(href: string): void {
    if (!href.startsWith('mailto:')) {
      return;
    }

    this.tracker.trackEvent({
      module: EventModule.Notification,
      journey: EventJourney.StatusMessage,
      action: EventAction.Clicked,
      metadata: {
        event: 'email-support',
        type: 'sync-error',
      },
    });

    this.openSupportEmailDraft();
  }

  private openSupportEmailDraft(): void {
    const email = this.emailBuilderService.buildSupportEmail({
      userId: this.globalStore.session()?.user.id,
      account: this.contextService.alias,
      email: this.oAuthService.mxUserEmail ?? undefined,
      platform: this.outlookService.platformLabel,
      outlookVersion:
        this.outlookService.mailbox?.diagnostics?.hostVersion ?? undefined,
      appVersion: this.contextService.version,
      browser: detectBrowser(navigator.userAgent),
      os: detectOS(navigator.userAgent),
    });

    this.emailBuilderService.open(email);
  }

  async reinstallOutlookSync(fromStatusMessage = false): Promise<void> {
    if (!this.loginDetails) {
      return;
    }

    if (fromStatusMessage) {
      this.tracker.trackEvent({
        module: EventModule.Notification,
        journey: EventJourney.StatusMessage,
        action: EventAction.Clicked,
        metadata: {
          event: 're-authenticate',
          type: 'sync-error',
        },
      });

      this.outlookStore.clearSyncHealthLastChecked();
      this.showSyncStatusMessage.set(false);
    }

    const useNewFlow = this.feature.isFeatureOn(
      'integration-o365-sync-configuration-shown',
      false,
    );

    if (!useNewFlow) {
      this.appDirectoryService
        .buildAppStoreFormRequest(this.loginDetails, true)
        .pipe(
          catchError(() => {
            this.notification().showMessage(
              'error',
              'outlook.errors.appStore',
              true,
            );
            return of(null);
          }),
        )
        .subscribe();
      return;
    }

    if (!tryClaimPopupOpen()) {
      return;
    }

    const popup = window.open('about:blank', '_blank');
    if (!popup) {
      releasePopupOpen();
      this.notification().showMessage('error', 'outlook.errors.appStore', true);
      return;
    }
    const handshakeNonce = generateHandshakeNonce();
    respondToHandshakeFromOpener(popup, {
      oAuthService: this.oAuthService,
      isCloud: this.outlookStore.isCloud(),
      nonce: handshakeNonce,
    });

    const syncConfigUrl = new URL(
      `/sync-configuration?reauth=1&${SYNC_CONFIG_HANDSHAKE_NONCE_PARAM}=${encodeURIComponent(handshakeNonce)}&${SYNC_CONFIG_LANG_PARAM}=${encodeURIComponent(this.outlookStore.language())}`,
      window.location.origin,
    ).toString();
    popup.location.href = syncConfigUrl;
  }

  dismissSyncStatus(): void {
    this.tracker.trackEvent({
      module: EventModule.Notification,
      journey: EventJourney.StatusMessage,
      action: EventAction.Clicked,
      metadata: {
        event: 'dismiss',
        type: 'sync-error',
      },
    });

    this.showSyncStatusMessage.set(false);
    this.dismissSyncStatusMessage.set(true);

    this.setSyncErrorDismissed();
  }

  private setSyncErrorDismissed(): void {
    const expirationTime = Date.now() + this.DISMISSAL_DURATION_MS;
    localStorage.setItem(
      this.SYNC_ERROR_DISMISSED_KEY,
      expirationTime.toString(),
    );
  }

  private clearSyncErrorDismissed(): void {
    localStorage.removeItem(this.SYNC_ERROR_DISMISSED_KEY);
  }

  private isSyncErrorDismissed(): boolean {
    const expirationTimeStr = localStorage.getItem(
      this.SYNC_ERROR_DISMISSED_KEY,
    );
    if (!expirationTimeStr) {
      return false;
    }
    const expirationTime = Number.parseInt(expirationTimeStr, 10);
    if (Number.isNaN(expirationTime) || Date.now() >= expirationTime) {
      this.clearSyncErrorDismissed();
      return false;
    }
    return true;
  }
}
